import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Page,
    Layout,
    Card,
    Text,
    InlineStack,
    BlockStack,
    Button,
    Banner,
    List
} from "@shopify/polaris";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip
} from "recharts";

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL || "",
    validateStatus: (status) => status >= 200 && status < 400
});

function formatMs(value) {
    return `${Number(value || 0).toFixed(0)}ms`;
}

function formatPct(value) {
    return `${Number(value || 0).toFixed(1)}%`;
}

function safeValue(value) {
    return value !== undefined && value !== null ? value : "—";
}

function buildEndpointChartData(endpoints) {
    return (endpoints || []).map((entry) => ({
        endpoint: entry.endpoint,
        averageMs: entry.averageMs || 0
    })).slice(0, 8);
}

export default function PerformanceDebugDashboard({ api, environment }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [nextRefresh, setNextRefresh] = useState(30);

    const backendApi = api || apiClient;

    const fetchMetrics = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await backendApi.get("/api/debug/perf-metrics");
            if (response.status >= 200 && response.status < 400 && response.data?.metrics) {
                setMetrics(response.data.metrics);
            } else {
                throw new Error(response.data?.error || `Status ${response.status}`);
            }
        } catch (err) {
            setError(`Unable to load perf metrics: ${err?.message || "unknown error"}`);
        } finally {
            setLoading(false);
            setNextRefresh(30);
        }
    };

    useEffect(() => {
        fetchMetrics();
    }, []);

    useEffect(() => {
        if (!autoRefresh) return undefined;

        const intervalId = setInterval(fetchMetrics, 15000);
        const countdownId = setInterval(() => {
            setNextRefresh((current) => (current > 0 ? current - 1 : 30));
        }, 1000);

        return () => {
            clearInterval(intervalId);
            clearInterval(countdownId);
        };
    }, [autoRefresh]);

    const overview = metrics?.overview || {};
    const endpoints = metrics?.endpoints || [];
    const cacheKeys = metrics?.cacheKeys || [];
    const slowRequests = metrics?.slowRequests || [];
    const recentEntries = metrics?.recentEntries || [];

    const endpointChartData = useMemo(() => buildEndpointChartData(endpoints), [endpoints]);

    return (
        <Page
            fullWidth
            title="Performance Dashboard"
            subtitle={environment === "production" ? "Perf metrics are disabled in production" : "Dev-only performance and cache metrics"}
            primaryAction={{ content: "Refresh", onAction: fetchMetrics, loading: loading }}
        >
            {environment === "production" ? (
                <Banner status="critical" title="Dev-only endpoint unavailable">
                    <p>Performance metrics are only available when the backend is running in development or staging mode.</p>
                </Banner>
            ) : null}

            {error ? <Banner status="critical">{error}</Banner> : null}

            <Layout>
                <Layout.Section>
                    <Card sectioned>
                        <BlockStack gap="200">
                            <InlineStack align="space-between" blockAlign="center">
                                <Text variant="headingMd">Overview</Text>
                                <InlineStack gap="200">
                                    <Button plain onClick={() => setAutoRefresh((current) => !current)}>
                                        {autoRefresh ? "Pause Auto-refresh" : "Enable Auto-refresh"}
                                    </Button>
                                    <Text variant="bodySm" tone="subdued">Next refresh in {nextRefresh}s</Text>
                                </InlineStack>
                            </InlineStack>
                            <List type="bullet">
                                <List.Item>
                                    <Text>
                                        {overview.totalTracked ?? 0} requests tracked, {overview.totalEndpoints ?? 0} endpoints, {overview.totalCacheKeys ?? 0} cache keys
                                    </Text>
                                </List.Item>
                                <List.Item>
                                    <Text>Average request duration: {formatMs(overview.averageMs)}</Text>
                                </List.Item>
                                <List.Item>
                                    <Text>Cache hit rate: {formatPct(overview.cacheHitRate)}</Text>
                                </List.Item>
                                <List.Item>
                                    <Text>Slow requests (>= 500ms): {overview.slowRequestCount ?? 0}</Text>
                                </List.Item>
                            </List>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card sectioned>
                        <Text variant="headingMd" as="h2">Endpoint Breakdown</Text>
                        {endpointChartData.length > 0 ? (
                            <div style={{ width: "100%", height: 320 }}>
                                <ResponsiveContainer>
                                    <BarChart data={endpointChartData} margin={{ top: 16, right: 12, left: 0, bottom: 12 }}>
                                        <XAxis dataKey="endpoint" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={80} />
                                        <YAxis />
                                        <Tooltip formatter={(value) => [formatMs(value), "Average"]} />
                                        <Bar dataKey="averageMs" fill="#5c6ac4" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <Text>No endpoint data available yet.</Text>
                        )}
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card sectioned>
                        <Text variant="headingMd" as="h2">Top Cache Keys</Text>
                        {cacheKeys.length > 0 ? (
                            <div style={{ display: "grid", gap: 12 }}>
                                {cacheKeys.slice(0, 8).map((item) => (
                                    <Card key={`${item.endpoint}-${item.cacheKey}`} sectioned>
                                        <Text variant="bodyMd" as="p">
                                            <strong>{item.endpoint}</strong> / <span style={{ opacity: 0.6 }}>{item.cacheKey}</span>
                                        </Text>
                                        <List type="bullet">
                                            <List.Item>Hits: {item.hits}</List.Item>
                                            <List.Item>Misses: {item.misses}</List.Item>
                                            <List.Item>Hit rate: {formatPct(item.hitRate)}</List.Item>
                                            <List.Item>Avg duration: {formatMs(item.averageMs)}</List.Item>
                                        </List>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Text>No cache key stats yet.</Text>
                        )}
                    </Card>
                </Layout.Section>

                <Layout.Section secondary>
                    <Card title="Slow Requests" sectioned>
                        {slowRequests.length ? (
                            <List>
                                {slowRequests.map((entry) => (
                                    <List.Item key={`${entry.timestamp}-${entry.endpoint}-${entry.durationMs}`}>
                                        <Text as="span">{new Date(entry.timestamp).toLocaleTimeString()} — {entry.endpoint} — {formatMs(entry.durationMs)}</Text>
                                    </List.Item>
                                ))}
                            </List>
                        ) : (
                            <Text>No slow requests recorded.</Text>
                        )}
                    </Card>
                </Layout.Section>

                <Layout.Section secondary>
                    <Card title="Recent Entries" sectioned>
                        {recentEntries.length ? (
                            <List>
                                {recentEntries.map((entry) => (
                                    <List.Item key={`${entry.timestamp}-${entry.endpoint}-${entry.cacheKey}`}>
                                        <Text as="span">
                                            {new Date(entry.timestamp).toLocaleTimeString()} — {entry.endpoint} — {entry.cacheKey} — {formatMs(entry.durationMs)} — {entry.isHit ? "HIT" : "MISS"}
                                        </Text>
                                    </List.Item>
                                ))}
                            </List>
                        ) : (
                            <Text>No recent entries yet.</Text>
                        )}
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
}
